/**
 * TUVI - Lista de Hojas de Vida (Cartera)
 * Vista principal de cartera con todas las hojas de vida de clientes
 */

import React, { useState, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
// xlsx-js-style: misma API de xlsx, pero permite dar estilo a las celdas
// (los links del Excel salen azules y subrayados, como en Gestión de Pagos).
import XLSX from 'xlsx-js-style';
import './Cartera.css';

const CarteraList = ({ onVerDetalle }) => {
  const { error, success } = useNotification();
  const [hojasVida, setHojasVida] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  // Los proyectos especiales llevan su propia pestaña, con las mismas hojas de vida
  const [tabCartera, setTabCartera] = useState('proyectos'); // 'proyectos' | 'especiales'

  // Modal de exportar
  const [modalExportar, setModalExportar] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Modal de nueva hoja de vida manual
  const [modalNueva, setModalNueva] = useState(false);
  const [creando, setCreando] = useState(false);
  const [formNueva, setFormNueva] = useState({
    cliente_nombre: '',
    proyecto_nombre: '',
    valor_final: '',
    telefono: '',
    correo: '',
    apartamento: '',
    observacion: '',
    poliza: ''
  });

  // Modal de importar Excel
  const [modalImportar, setModalImportar] = useState(false);
  const [importando, setImportando] = useState(false);
  const [registrosPreview, setRegistrosPreview] = useState([]);
  const [resultadoImport, setResultadoImport] = useState(null);

  useEffect(() => {
    cargarHojasVida();
  }, []);

  const cargarHojasVida = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{ carteraHojasVida { hojas_vida { id proyecto_id notas finalizado observacion pago_proxima_semana valor_ejecutado delta_caja descuentos proyecto_nombre cliente_nombre cliente_email cliente_telefono apartamento valor_final estado total_recibido total_esperado pendiente porcentaje_pagado } } }`);
      setHojasVida(data.carteraHojasVida.hojas_vida || []);
    } catch (err) {
      console.error('Error cargando cartera:', err);
      error('Error al cargar la cartera');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatCurrencyExcel = (value) => {
    return `$ ${new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0)}`;
  };

  const getEstadoColor = (porcentaje) => {
    if (porcentaje >= 100) return 'completado';
    if (porcentaje >= 50) return 'parcial';
    if (porcentaje > 0) return 'iniciado';
    return 'pendiente';
  };

  const getEstadoProyectoLabel = (estado) => {
    const estados = {
      'contratado_no_iniciado': 'No Iniciado',
      'en_ejecucion': 'En Ejecución',
      'finalizado': 'Finalizado'
    };
    return estados[estado] || estado;
  };

  const getEstadoCobroLabel = (estado) => {
    const estados = {
      'sin_enviar': 'Sin Enviar',
      'pendiente_pago': 'Pendiente de Pago',
      'pagado': 'Pagado'
    };
    return estados[estado] || estado;
  };

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Exportar a Excel usando XLSX
  const exportarExcel = async (estadoExportar) => {
    setExportando(true);
    try {
      // Se exporta lo de la pestaña activa (proyectos o proyectos especiales)
      let hojasFiltradas = hojasVida.filter(hv => (tabCartera === 'especiales' ? esEspecial(hv) : !esEspecial(hv)));

      if (estadoExportar === 'sin_pagos') {
        hojasFiltradas = hojasFiltradas.filter(hv => hv.total_recibido === 0);
      } else if (estadoExportar === 'pagos_parciales') {
        hojasFiltradas = hojasFiltradas.filter(hv => hv.total_recibido > 0 && hv.porcentaje_pagado < 100);
      } else if (estadoExportar === 'pagos_completos') {
        hojasFiltradas = hojasFiltradas.filter(hv => hv.porcentaje_pagado >= 100);
      }

      if (hojasFiltradas.length === 0) {
        error('No hay datos para exportar con el filtro seleccionado');
        setExportando(false);
        return;
      }

      // Cargar detalle de cada hoja de vida para obtener los campos de pago
      const hojasConDetalle = [];
      for (const hv of hojasFiltradas) {
        try {
          const data = await client.query(`query($id: Int!) { carteraDetalle(id: $id) { hoja_vida { id proyecto_id proyecto_nombre apartamento cliente_nombre cliente_email cliente_telefono estado subtotal aiu iva notas valor_final total_recibido total_esperado pendiente porcentaje_pagado campos { id nombre porcentaje valor_calculado valor_recibido pendiente estado_cobro fecha_pago cuenta_cobro_archivo cuenta_cobro_fecha_envio comprobante_pago_archivo comprobantes { id descripcion monto nombre_archivo ruta_archivo fecha_pago } } } } }`, { id: hv.id });
          if (data.carteraDetalle?.hoja_vida) {
            hojasConDetalle.push(data.carteraDetalle.hoja_vida);
          }
        } catch (e) {
          console.error(`Error cargando detalle de hoja ${hv.id}:`, e);
        }
      }

      // Links de los adjuntos (cuentas de cobro y comprobantes de pago). Llevan un
      // token propio del archivo, con vencimiento: el Excel se puede compartir
      // sin repartir la sesión.
      const mapaLinks = new Map();
      try {
        const d = await client.query(
          `query($ids: [Int!]!) { linksArchivosCartera(hoja_vida_ids: $ids) { links { clave url } } }`,
          { ids: hojasConDetalle.map(h => h.id) });
        (d.linksArchivosCartera?.links || []).forEach(l => { if (l.url) mapaLinks.set(l.clave, l.url); });
      } catch (e) {
        console.error('No se pudieron generar los links de los adjuntos:', e);
        error('El Excel saldrá sin los links de los adjuntos');
      }
      const linkDe = (clave) => mapaLinks.get((clave || '').trim()) || '';
      // Fecha de pago sin corrimiento de zona horaria. Las fechas "solo día" se
      // guardan a medianoche UTC: si se muestran en hora Colombia saldría el día
      // anterior, así que esas se formatean en UTC. Las que traen hora real
      // (marcado pagado desde la cuenta de cobro) se muestran en hora local.
      const fmtFechaPago = (f) => {
        if (!f) return '-';
        const m = String(f).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
        const ms = Number(f);
        const d = new Date(Number.isFinite(ms) ? ms : f);
        if (isNaN(d)) return String(f);
        const soloDia = Number.isFinite(ms) && ms % 86400000 === 0;
        return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit', ...(soloDia ? { timeZone: 'UTC' } : {}) });
      };
      // Celdas que llevan hipervínculo: se aplican después de crear cada hoja
      const linksDetalle = [];
      const linksPagos = [];
      const nombreArchivoDe = (clave) => (clave || '').split('/').pop() || '-';

      const filtroLabel = {
        'sin_pagos': 'SIN PAGOS',
        'pagos_parciales': 'PAGOS PARCIALES',
        'pagos_completos': 'PAGOS COMPLETOS',
        'todos': 'TODOS LOS ESTADOS'
      };

      // Calcular totales globales
      const totalEsperadoGlobal = hojasConDetalle.reduce((sum, hv) => sum + (hv.total_esperado || 0), 0);
      const totalRecibidoGlobal = hojasConDetalle.reduce((sum, hv) => sum + (hv.total_recibido || 0), 0);
      const totalPendienteGlobal = totalEsperadoGlobal - totalRecibidoGlobal;

      // === HOJA 1: Resumen General ===
      const resumenData = [
        ['REPORTE DE CARTERA - TUVI'],
        [''],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO')],
        ['Hora:', new Date().toLocaleTimeString('es-CO')],
        ['Filtro:', filtroLabel[estadoExportar] || 'TODOS'],
        ['Total de Clientes:', hojasConDetalle.length],
        [''],
        ['═══════════════════════════════════════════════════════════════'],
        ['RESUMEN GLOBAL'],
        ['═══════════════════════════════════════════════════════════════'],
        [''],
        ['Total Esperado:', formatCurrencyExcel(totalEsperadoGlobal)],
        ['Total Recibido:', formatCurrencyExcel(totalRecibidoGlobal)],
        ['Total Pendiente:', formatCurrencyExcel(totalPendienteGlobal)],
        [''],
        ['═══════════════════════════════════════════════════════════════'],
        ['LISTADO DE CLIENTES'],
        ['═══════════════════════════════════════════════════════════════'],
        [''],
        ['Cliente', 'Proyecto', 'Valor Final', 'Total Esperado', 'Total Recibido', 'Pendiente', '% Pagado', 'Estado Proyecto']
      ];

      hojasConDetalle.forEach(hv => {
        resumenData.push([
          hv.cliente_nombre || '',
          hv.proyecto_nombre || '',
          formatCurrencyExcel(hv.valor_final),
          formatCurrencyExcel(hv.total_esperado),
          formatCurrencyExcel(hv.total_recibido),
          formatCurrencyExcel(hv.pendiente),
          `${hv.porcentaje_pagado || 0}%`,
          getEstadoProyectoLabel(hv.estado)
        ]);
      });

      // === HOJA 2: Detalle Completo ===
      const detalleData = [
        ['DETALLE COMPLETO DE CARTERA - TUVI'],
        [''],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO'), '', 'Filtro:', filtroLabel[estadoExportar] || 'TODOS'],
        ['']
      ];

      hojasConDetalle.forEach((hv, index) => {
        // Separador entre clientes
        if (index > 0) {
          detalleData.push(['']);
          detalleData.push(['']);
        }

        detalleData.push(['═══════════════════════════════════════════════════════════════════════════════════════════════════']);
        detalleData.push([`CLIENTE ${index + 1}: ${hv.cliente_nombre}`]);
        detalleData.push(['═══════════════════════════════════════════════════════════════════════════════════════════════════']);
        detalleData.push(['']);

        // Info del cliente
        detalleData.push(['INFORMACIÓN DEL CLIENTE']);
        detalleData.push(['Nombre:', hv.cliente_nombre || 'N/A']);
        detalleData.push(['Email:', hv.cliente_email || 'N/A']);
        detalleData.push(['Teléfono:', hv.cliente_telefono || 'N/A']);
        detalleData.push(['']);

        // Info del proyecto
        detalleData.push(['INFORMACIÓN DEL PROYECTO']);
        detalleData.push(['Proyecto:', hv.proyecto_nombre || 'N/A']);
        detalleData.push(['Estado:', getEstadoProyectoLabel(hv.estado)]);
        detalleData.push(['Subtotal:', formatCurrencyExcel(hv.subtotal)]);
        detalleData.push(['AIU (15%):', formatCurrencyExcel(hv.aiu)]);
        detalleData.push(['IVA:', formatCurrencyExcel(hv.iva)]);
        detalleData.push(['Valor Final:', formatCurrencyExcel(hv.valor_final)]);
        detalleData.push(['']);

        // Resumen de cartera
        detalleData.push(['RESUMEN DE CARTERA']);
        detalleData.push(['Total Esperado:', formatCurrencyExcel(hv.total_esperado)]);
        detalleData.push(['Total Recibido:', formatCurrencyExcel(hv.total_recibido)]);
        detalleData.push(['Pendiente por Cobrar:', formatCurrencyExcel(hv.pendiente)]);
        detalleData.push(['Porcentaje Pagado:', `${hv.porcentaje_pagado || 0}%`]);
        detalleData.push(['']);

        // Tabla de campos de pago
        detalleData.push(['DETALLE DE PAGOS']);
        detalleData.push([
          'Campo de Pago',
          'Porcentaje',
          'Valor Esperado',
          'Valor Recibido',
          'Pendiente',
          'Estado Cobro',
          'Fecha Envío Cuenta',
          'Fecha Pago',
          'Cuenta de cobro',
          'Comprobante (marcado pagado)'
        ]);

        if (hv.campos && hv.campos.length > 0) {
          hv.campos.forEach(campo => {
            const pendienteCampo = (campo.valor_calculado || 0) - (campo.valor_recibido || 0);
            const lCuenta = linkDe(campo.cuenta_cobro_archivo);
            const lMarcado = linkDe(campo.comprobante_pago_archivo);
            const fila = detalleData.length;
            detalleData.push([
              campo.nombre || '',
              `${campo.porcentaje || 0}%`,
              formatCurrencyExcel(campo.valor_calculado),
              formatCurrencyExcel(campo.valor_recibido),
              formatCurrencyExcel(pendienteCampo),
              getEstadoCobroLabel(campo.estado_cobro),
              formatDate(campo.cuenta_cobro_fecha_envio),
              fmtFechaPago(campo.fecha_pago),
              lCuenta ? 'Ver cuenta de cobro' : '-',
              lMarcado ? 'Ver comprobante' : '-'
            ]);
            if (lCuenta) linksDetalle.push({ r: fila, c: 8, url: lCuenta });
            if (lMarcado) linksDetalle.push({ r: fila, c: 9, url: lMarcado });
          });

          // Pagos registrados uno a uno (modal "Registrar Pago"): monto, fecha,
          // descripción y el comprobante adjunto, con su link.
          const pagosHv = hv.campos.flatMap(campo => (campo.comprobantes || []).map(p => ({ campo, p })));
          detalleData.push(['']);
          detalleData.push(['PAGOS REGISTRADOS']);
          detalleData.push(['Campo de Pago', 'Fecha Pago', 'Monto', 'Descripción', 'Archivo adjunto', 'Comprobante']);
          if (pagosHv.length > 0) {
            pagosHv.forEach(({ campo, p }) => {
              const lp = linkDe(p.ruta_archivo);
              const fila = detalleData.length;
              detalleData.push([
                campo.nombre || '',
                fmtFechaPago(p.fecha_pago),
                formatCurrencyExcel(p.monto),
                p.descripcion || '-',
                p.nombre_archivo || nombreArchivoDe(p.ruta_archivo),
                lp ? 'Ver comprobante' : (p.ruta_archivo ? 'Sin link' : 'Sin adjunto')
              ]);
              if (lp) {
                linksDetalle.push({ r: fila, c: 4, url: lp }); // el nombre del archivo también abre el documento
                linksDetalle.push({ r: fila, c: 5, url: lp });
              }
            });
          } else {
            detalleData.push(['No hay pagos registrados']);
          }
        } else {
          detalleData.push(['No hay campos de pago registrados']);
        }

        // Notas
        if (hv.notas) {
          detalleData.push(['']);
          detalleData.push(['NOTAS:', hv.notas]);
        }
      });

      // === HOJA 3: Solo Pagos Pendientes ===
      const pendientesData = [
        ['PAGOS PENDIENTES DE COBRO - TUVI'],
        [''],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO')],
        [''],
        ['Cliente', 'Proyecto', 'Campo de Pago', 'Valor Esperado', 'Valor Recibido', 'Pendiente', 'Estado Cobro', 'Fecha Envío']
      ];

      hojasConDetalle.forEach(hv => {
        if (hv.campos && hv.campos.length > 0) {
          hv.campos.forEach(campo => {
            const pendienteCampo = (campo.valor_calculado || 0) - (campo.valor_recibido || 0);
            if (pendienteCampo > 0) {
              pendientesData.push([
                hv.cliente_nombre || '',
                hv.proyecto_nombre || '',
                campo.nombre || '',
                formatCurrencyExcel(campo.valor_calculado),
                formatCurrencyExcel(campo.valor_recibido),
                formatCurrencyExcel(pendienteCampo),
                getEstadoCobroLabel(campo.estado_cobro),
                formatDate(campo.cuenta_cobro_fecha_envio)
              ]);
            }
          });
        }
      });

      if (pendientesData.length === 5) {
        pendientesData.push(['No hay pagos pendientes']);
      }

      // === HOJA 4: Pagos registrados (plana, un pago por fila) ===
      const pagosData = [
        ['PAGOS REGISTRADOS - TUVI'],
        [''],
        ['Fecha de Exportación:', new Date().toLocaleDateString('es-CO'), '', 'Filtro:', filtroLabel[estadoExportar] || 'TODOS'],
        ['Los links de los comprobantes vencen a los 30 días de generado este archivo.'],
        [''],
        ['Cliente', 'Proyecto', 'Apto', 'Campo de Pago', 'Fecha Pago', 'Monto', 'Descripción', 'Archivo adjunto', 'Comprobante']
      ];
      const filasPagosInicio = pagosData.length;
      let totalPagosRegistrados = 0;

      hojasConDetalle.forEach(hv => {
        (hv.campos || []).forEach(campo => {
          (campo.comprobantes || []).forEach(p => {
            const lp = linkDe(p.ruta_archivo);
            const fila = pagosData.length;
            pagosData.push([
              hv.cliente_nombre || '',
              hv.proyecto_nombre || '',
              hv.apartamento || '',
              campo.nombre || '',
              fmtFechaPago(p.fecha_pago),
              formatCurrencyExcel(p.monto),
              p.descripcion || '-',
              p.nombre_archivo || nombreArchivoDe(p.ruta_archivo),
              lp ? 'Ver comprobante' : (p.ruta_archivo ? 'Sin link' : 'Sin adjunto')
            ]);
            totalPagosRegistrados += Number(p.monto || 0);
            if (lp) {
              linksPagos.push({ r: fila, c: 7, url: lp });
              linksPagos.push({ r: fila, c: 8, url: lp });
            }
          });
          // Pago hecho al marcar pagada la cuenta de cobro (no pasa por el modal
          // de Registrar Pago, pero también es plata recibida con su comprobante).
          if ((campo.comprobantes || []).length === 0 && campo.comprobante_pago_archivo && Number(campo.valor_recibido || 0) > 0) {
            const lm = linkDe(campo.comprobante_pago_archivo);
            const fila = pagosData.length;
            pagosData.push([
              hv.cliente_nombre || '',
              hv.proyecto_nombre || '',
              hv.apartamento || '',
              campo.nombre || '',
              fmtFechaPago(campo.fecha_pago),
              formatCurrencyExcel(campo.valor_recibido),
              'Pago de cuenta de cobro (marcado como pagado)',
              nombreArchivoDe(campo.comprobante_pago_archivo),
              lm ? 'Ver comprobante' : 'Sin link'
            ]);
            totalPagosRegistrados += Number(campo.valor_recibido || 0);
            if (lm) {
              linksPagos.push({ r: fila, c: 7, url: lm });
              linksPagos.push({ r: fila, c: 8, url: lm });
            }
          }
        });
      });

      if (pagosData.length === filasPagosInicio) {
        pagosData.push(['No hay pagos registrados']);
      } else {
        pagosData.push(['']);
        pagosData.push(['', '', '', '', 'Total pagos:', formatCurrencyExcel(totalPagosRegistrados)]);
      }

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      // Los links quedan como hipervínculo real, clicable desde Excel, y con el
      // estilo de enlace (azul, subrayado) para que se vea que se puede abrir.
      const estiloLink = { font: { sz: 11, color: { rgb: '1565C0' }, underline: true } };
      const aplicarLinks = (ws, lista) => {
        lista.forEach(({ r, c, url }) => {
          const celda = XLSX.utils.encode_cell({ r, c });
          if (ws[celda]) {
            ws[celda].l = { Target: url, Tooltip: 'Abrir documento' };
            ws[celda].s = estiloLink;
          }
        });
      };

      // Hoja 1: Resumen
      const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
      wsResumen['!cols'] = [
        { wch: 30 }, { wch: 35 }, { wch: 18 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

      // Hoja 2: Detalle
      const wsDetalle = XLSX.utils.aoa_to_sheet(detalleData);
      wsDetalle['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 18 }, { wch: 30 },
        { wch: 26 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
        { wch: 20 }, { wch: 26 }
      ];
      aplicarLinks(wsDetalle, linksDetalle);
      XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle Completo');

      // Hoja 3: Pendientes
      const wsPendientes = XLSX.utils.aoa_to_sheet(pendientesData);
      wsPendientes['!cols'] = [
        { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPendientes, 'Pagos Pendientes');

      // Hoja 4: Pagos registrados
      const wsPagos = XLSX.utils.aoa_to_sheet(pagosData);
      wsPagos['!cols'] = [
        { wch: 30 }, { wch: 30 }, { wch: 10 }, { wch: 22 }, { wch: 14 },
        { wch: 18 }, { wch: 36 }, { wch: 30 }, { wch: 18 }
      ];
      aplicarLinks(wsPagos, linksPagos);
      XLSX.utils.book_append_sheet(wb, wsPagos, 'Pagos Registrados');

      // Generar nombre de archivo
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Cartera_TUVI_${filtroLabel[estadoExportar].replace(/ /g, '_')}_${fecha}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(wb, nombreArchivo);

      success('Reporte exportado correctamente');
      setModalExportar(false);

    } catch (err) {
      console.error('Error al exportar Excel:', err);
      error('Error al generar el archivo Excel');
    } finally {
      setExportando(false);
    }
  };

  // Hojas de la pestaña activa: los proyectos especiales van aparte
  const esEspecial = (hv) => hv.estado === 'proyecto_especial';
  const hojasDelTab = hojasVida.filter(hv => (tabCartera === 'especiales' ? esEspecial(hv) : !esEspecial(hv)));
  const totalEspeciales = hojasVida.filter(esEspecial).length;

  // Filtrar hojas de vida
  const hojasFiltradas = hojasDelTab.filter(hv => {
    const term = filtro.toLowerCase().trim();
    const matchTexto = term === '' ||
      hv.cliente_nombre?.toLowerCase().includes(term) ||
      hv.proyecto_nombre?.toLowerCase().includes(term) ||
      String(hv.apartamento || '').toLowerCase().includes(term);

    const matchEstado = estadoFiltro === 'todos' ||
      (estadoFiltro === 'pendiente' && hv.porcentaje_pagado === 0) ||
      (estadoFiltro === 'parcial' && hv.porcentaje_pagado > 0 && hv.porcentaje_pagado < 100) ||
      (estadoFiltro === 'completado' && hv.porcentaje_pagado >= 100);

    return matchTexto && matchEstado;
  });

  const crearHojaVidaManual = async () => {
    if (!formNueva.cliente_nombre.trim() || !formNueva.proyecto_nombre.trim() || !formNueva.valor_final) {
      error('Cliente, proyecto y valor final son obligatorios');
      return;
    }
    setCreando(true);
    try {
      // 1. Crear proyecto
      const valorFinal = parseFloat(formNueva.valor_final.toString().replace(/\./g, '').replace(',', '.'));
      const proyR = await client.mutate(`mutation($input: CrearProyectoInput!) { crearProyecto(input: $input) { success proyecto_id } }`, {
        input: { proyecto: { nombre_cliente: formNueva.cliente_nombre.trim(), proyecto_nombre: formNueva.proyecto_nombre.trim(), telefono: formNueva.telefono.trim(), correo: formNueva.correo.trim(), apartamento: formNueva.apartamento.trim(), valor_final: valorFinal } }
      });
      if (!proyR.crearProyecto.success) { error('Error al crear proyecto'); setCreando(false); return; }

      // 2. La hoja de vida se crea automáticamente en crearProyecto
      const data = { success: true };
      if (data.success) {
        success('Hoja de vida creada exitosamente');
        setModalNueva(false);
        setFormNueva({ cliente_nombre: '', proyecto_nombre: '', valor_final: '', telefono: '', correo: '', apartamento: '', observacion: '', poliza: '' });
        cargarHojasVida();
      } else {
        error(data.message || 'Error al crear hoja de vida');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setCreando(false);
    }
  };

  // Parsear Excel de cartera
  const parsearExcelCartera = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });

        // Fila 1 (index 1) son los headers
        const headerRow = rows[1] || [];
        if (!headerRow[2] || !String(headerRow[2]).includes('PROPIETARIO')) {
          error('Formato de Excel no reconocido. Se espera la columna PROPIETARIO en la posición C.');
          return;
        }

        const excelToDate = (serial) => {
          if (!serial || typeof serial !== 'number' || serial < 1000) return null;
          const d = new Date((serial - 25569) * 86400 * 1000);
          return d.toISOString().split('T')[0];
        };

        const registros = [];
        for (let i = 2; i < rows.length; i++) {
          const r = rows[i];
          const propietario = r[2] ? String(r[2]).trim() : '';
          if (!propietario) continue;

          // Extraer pagos (pares fecha+monto en cols 18-41)
          const pagos = [];
          const pagosCols = [
            [18, 19], [20, 21], [22, 23], [24, 25], [26, 27],
            [28, 29], [30, 31], [32, 33], [34, 35], [36, 37],
            [38, null], [39, null], [40, null], [41, null]
          ];
          let numPago = 0;
          for (const [fechaCol, montoCol] of pagosCols) {
            const monto = montoCol !== null ? r[montoCol] : r[fechaCol];
            if (monto && typeof monto === 'number' && monto > 0) {
              numPago++;
              const fecha = montoCol !== null ? excelToDate(r[fechaCol]) : null;
              pagos.push({ numero: numPago, fecha, monto });
            }
          }

          registros.push({
            estado: r[0] ? String(r[0]).trim() : '',
            nit: r[1] ? String(r[1]).trim() : '',
            propietario,
            proyecto: r[3] ? String(r[3]).trim() : '',
            poliza: r[4] ? String(r[4]).trim() : '',
            finalizado: r[5] ? String(r[5]).trim() : 'NO',
            apartamento: r[6] ? String(r[6]).trim() : '',
            porcentaje_pago: typeof r[7] === 'number' ? r[7] : 0,
            pago_proxima_semana: typeof r[8] === 'number' ? r[8] : 0,
            observacion: r[9] ? String(r[9]).trim() : '',
            tarea_naty: r[10] ? String(r[10]).trim() : '',
            tarea_luisa: r[11] ? String(r[11]).trim() : '',
            valor_total: typeof r[12] === 'number' ? r[12] : 0,
            valor_pagado: typeof r[13] === 'number' ? r[13] : 0,
            faltante: typeof r[14] === 'number' ? r[14] : 0,
            valor_ejecutado: typeof r[15] === 'number' ? r[15] : 0,
            delta_caja: typeof r[16] === 'number' ? r[16] : 0,
            fecha_inicio_apto: excelToDate(r[17]),
            descuentos: typeof r[42] === 'number' ? r[42] : 0,
            valor_ejecutado_admin: r[43] ? String(r[43]).trim() : '',
            fecha_inicio_obra: excelToDate(r[44]),
            fecha_fin_obra: excelToDate(r[45]),
            formatos_admin: r[46] ? String(r[46]).trim() : '',
            seguro: r[47] ? String(r[47]).trim() : '',
            deposito: r[48] ? String(r[48]).trim() : '',
            contrato_firmado: r[49] ? String(r[49]).trim() : '',
            llaves: r[50] ? String(r[50]).trim() : '',
            nota: r[51] ? String(r[51]).trim() : '',
            pagos
          });
        }

        setRegistrosPreview(registros);
        setResultadoImport(null);
      } catch (err) {
        console.error('Error parseando Excel:', err);
        error('Error al leer el archivo Excel');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const ejecutarImportacion = async () => {
    if (registrosPreview.length === 0) return;
    setImportando(true);
    setResultadoImport(null);
    try {
      // Importar cada registro creando proyecto + hoja de vida via GraphQL
      let creados = 0;
      for (const reg of registrosPreview) {
        try {
          const valorFinal = parseFloat(reg.valor_final) || 0;
          await client.mutate(`mutation($input: CrearProyectoInput!) { crearProyecto(input: $input) { success proyecto_id } }`, {
            input: { proyecto: { nombre_cliente: reg.cliente_nombre || '', proyecto_nombre: reg.proyecto_nombre || '', valor_final: valorFinal, apartamento: reg.apartamento || '' } }
          });
          creados++;
        } catch (e) { console.error('Error importando:', e); }
      }
      const data = { success: true, creados };
      if (data.success) {
        setResultadoImport(data);
        success(`Importacion completada: ${creados} creados`);
        cargarHojasVida();
      } else {
        error(data.message || 'Error en la importacion');
      }
    } catch (err) {
      error('Error de conexion al importar');
      console.error(err);
    } finally {
      setImportando(false);
    }
  };

  // Calcular totales
  const totalEsperado = hojasFiltradas.reduce((sum, hv) => sum + (hv.total_esperado || 0), 0);
  const totalRecibido = hojasFiltradas.reduce((sum, hv) => sum + (hv.total_recibido || 0), 0);
  const totalPendiente = totalEsperado - totalRecibido;

  if (loading) {
    return (
      <div className="cartera-loading">
        <div className="spinner"></div>
        <p>Cargando cartera...</p>
      </div>
    );
  }

  return (
    <div className="cartera-container">
      {/* Header */}
      <div className="cartera-header">
        <div className="header-titulo">
          <h1>Cartera</h1>
          <p>Hojas de vida y seguimiento de pagos de clientes</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-importar-excel" onClick={() => { setModalImportar(true); setRegistrosPreview([]); setResultadoImport(null); }}>
            Importar Excel
          </button>
          <button className="btn-nueva-hoja" onClick={() => setModalNueva(true)}>
            + Nueva Hoja de Vida
          </button>
          <button className="btn-exportar-excel" onClick={() => setModalExportar(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Pestañas: proyectos normales y proyectos especiales */}
      <div className="cartera-tabs">
        <button
          className={`cartera-tab ${tabCartera === 'proyectos' ? 'activa' : ''}`}
          onClick={() => setTabCartera('proyectos')}
        >
          Proyectos ({hojasVida.length - totalEspeciales})
        </button>
        <button
          className={`cartera-tab ${tabCartera === 'especiales' ? 'activa' : ''}`}
          onClick={() => setTabCartera('especiales')}
        >
          Proyectos Especiales ({totalEspeciales})
        </button>
      </div>

      {/* Estadísticas generales */}
      <div className="cartera-stats">
        <div className="stat-card total">
          <span className="stat-icon">💰</span>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(totalEsperado)}</span>
            <span className="stat-label">Total Esperado</span>
          </div>
        </div>
        <div className="stat-card recibido">
          <span className="stat-icon">✅</span>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(totalRecibido)}</span>
            <span className="stat-label">Total Recibido</span>
          </div>
        </div>
        <div className="stat-card pendiente">
          <span className="stat-icon">⏳</span>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(totalPendiente)}</span>
            <span className="stat-label">Por Cobrar</span>
          </div>
        </div>
        <div className="stat-card clientes">
          <span className="stat-icon">👥</span>
          <div className="stat-info">
            <span className="stat-value">{hojasFiltradas.length}</span>
            <span className="stat-label">Clientes</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="cartera-filtros">
        <div className="filtro-busqueda">
          <input
            type="text"
            placeholder="Buscar por cliente, proyecto o # apartamento..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
        <div className="filtro-estado">
          <select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
            <option value="todos">Todos los estados</option>
            <option value="pendiente">Sin pagos</option>
            <option value="parcial">Pagos parciales</option>
            <option value="completado">Pagado completo</option>
          </select>
        </div>
      </div>

      {/* Lista de hojas de vida */}
      {hojasFiltradas.length === 0 ? (
        <div className="cartera-vacia">
          <span className="icono">📋</span>
          <p>{tabCartera === 'especiales' ? 'No hay hojas de vida de proyectos especiales' : 'No hay hojas de vida registradas'}</p>
          <span className="subtexto">
            {tabCartera === 'especiales'
              ? 'Se crean solas al registrar el proyecto en Proyectos Especiales'
              : 'Las hojas de vida se crean automáticamente al registrar un proyecto'}
          </span>
        </div>
      ) : (
        <div className="hojas-vida-grid">
          {hojasFiltradas.map(hv => (
            <div
              key={hv.id}
              className={`hoja-vida-card ${getEstadoColor(hv.porcentaje_pagado)}`}
              onClick={() => onVerDetalle && onVerDetalle(hv.id)}
            >
              <div className="hv-header">
                <div className="hv-cliente">
                  <h3>{hv.cliente_nombre}</h3>
                  <span className="hv-proyecto">{hv.proyecto_nombre}{hv.apartamento ? ` - Apto ${hv.apartamento}` : ''}</span>
                </div>
                <div className="hv-badges">
                  {hv.finalizado === 'SI' && <span className="badge-finalizado">Finalizado</span>}
                  <span className={`hv-estado-proyecto ${hv.estado}`}>
                    {getEstadoProyectoLabel(hv.estado)}
                  </span>
                </div>
              </div>

              <div className="hv-progreso">
                <div className="progreso-barra">
                  <div
                    className="progreso-fill"
                    style={{ width: `${Math.min(hv.porcentaje_pagado, 100)}%` }}
                  ></div>
                </div>
                <span className="progreso-porcentaje">{hv.porcentaje_pagado.toFixed(1)}%</span>
              </div>

              <div className="hv-valores">
                <div className="hv-valor">
                  <span className="label">Valor Proyecto</span>
                  <span className="valor">{formatCurrency(hv.valor_final)}</span>
                </div>
                <div className="hv-valor">
                  <span className="label">Recibido</span>
                  <span className="valor recibido">{formatCurrency(hv.total_recibido)}</span>
                </div>
                <div className="hv-valor">
                  <span className="label">Pendiente</span>
                  <span className="valor pendiente">{formatCurrency(hv.pendiente)}</span>
                </div>
              </div>

              {hv.observacion && (
                <div className="hv-observacion" title={hv.observacion}>
                  {hv.observacion.length > 60 ? hv.observacion.substring(0, 60) + '...' : hv.observacion}
                </div>
              )}

              <div className="hv-contacto">
                {hv.cliente_email && <span>📧 {hv.cliente_email}</span>}
                {hv.cliente_telefono && <span>📱 {hv.cliente_telefono}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Nueva Hoja de Vida Manual */}
      {modalNueva && (
        <div className="modal-overlay" onClick={() => !creando && setModalNueva(false)}>
          <div className="modal-exportar" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nueva Hoja de Vida</h3>
              <button className="btn-cerrar" onClick={() => setModalNueva(false)} disabled={creando}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-descripcion" style={{ marginBottom: '16px' }}>
                Crea una hoja de vida manualmente para llevar el control de cartera de cualquier proyecto.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Cliente *
                  </label>
                  <input
                    type="text"
                    placeholder="Nombre del cliente"
                    value={formNueva.cliente_nombre}
                    onChange={(e) => setFormNueva(p => ({ ...p, cliente_nombre: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Proyecto *
                  </label>
                  <input
                    type="text"
                    placeholder="Nombre del proyecto"
                    value={formNueva.proyecto_nombre}
                    onChange={(e) => setFormNueva(p => ({ ...p, proyecto_nombre: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Valor Final del Proyecto * (COP)
                  </label>
                  <input
                    type="number"
                    placeholder="Ej: 50000000"
                    value={formNueva.valor_final}
                    onChange={(e) => setFormNueva(p => ({ ...p, valor_final: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                      Teléfono
                    </label>
                    <input
                      type="text"
                      placeholder="Teléfono"
                      value={formNueva.telefono}
                      onChange={(e) => setFormNueva(p => ({ ...p, telefono: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                      Correo
                    </label>
                    <input
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={formNueva.correo}
                      onChange={(e) => setFormNueva(p => ({ ...p, correo: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                      Apartamento
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: 401, Casa 5"
                      value={formNueva.apartamento}
                      onChange={(e) => setFormNueva(p => ({ ...p, apartamento: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                      Poliza
                    </label>
                    <input
                      type="text"
                      placeholder="Numero de poliza"
                      value={formNueva.poliza}
                      onChange={(e) => setFormNueva(p => ({ ...p, poliza: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Observacion
                  </label>
                  <textarea
                    placeholder="Notas iniciales de seguimiento..."
                    value={formNueva.observacion}
                    onChange={(e) => setFormNueva(p => ({ ...p, observacion: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => setModalNueva(false)}
                  disabled={creando}
                  style={{ padding: '8px 18px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={crearHojaVidaManual}
                  disabled={creando}
                  style={{ padding: '8px 18px', borderRadius: '6px', border: 'none', background: '#059669', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                >
                  {creando ? 'Creando...' : 'Crear Hoja de Vida'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Importar Excel */}
      {modalImportar && (
        <div className="modal-overlay" onClick={() => !importando && setModalImportar(false)}>
          <div className="modal-exportar modal-importar" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Importar Cartera desde Excel</h3>
              <button className="btn-cerrar" onClick={() => setModalImportar(false)} disabled={importando}>×</button>
            </div>
            <div className="modal-body">
              {!resultadoImport ? (
                <>
                  <p className="modal-descripcion">
                    Seleccione el archivo Excel de cartera (.xlsx). El sistema cruzara los datos por Cliente + Proyecto + Apartamento.
                  </p>

                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => e.target.files[0] && parsearExcelCartera(e.target.files[0])}
                    style={{ marginBottom: '16px', padding: '8px', border: '2px dashed #d1d5db', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}
                  />

                  {registrosPreview.length > 0 && (
                    <>
                      <div className="import-preview-header">
                        <strong>{registrosPreview.length} registros encontrados</strong>
                      </div>
                      <div className="import-preview-table">
                        <table>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Propietario</th>
                              <th>Proyecto</th>
                              <th>Apto</th>
                              <th>Valor Total</th>
                              <th>Pagos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {registrosPreview.slice(0, 20).map((r, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.propietario}</td>
                                <td>{r.proyecto}</td>
                                <td>{r.apartamento}</td>
                                <td>{formatCurrency(r.valor_total)}</td>
                                <td>{r.pagos.length}</td>
                              </tr>
                            ))}
                            {registrosPreview.length > 20 && (
                              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280' }}>... y {registrosPreview.length - 20} mas</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                        <button onClick={() => setModalImportar(false)} disabled={importando}
                          style={{ padding: '8px 18px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>
                          Cancelar
                        </button>
                        <button onClick={ejecutarImportacion} disabled={importando}
                          className="btn-confirmar-import">
                          {importando ? 'Importando...' : `Importar ${registrosPreview.length} registros`}
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="import-resultado">
                  <h4>Importacion Completada</h4>
                  <div className="import-stats">
                    <div className="import-stat creados"><span>{resultadoImport.creados}</span> Creados</div>
                    <div className="import-stat actualizados"><span>{resultadoImport.actualizados}</span> Actualizados</div>
                    {resultadoImport.errores?.length > 0 && (
                      <div className="import-stat errores"><span>{resultadoImport.errores.length}</span> Errores</div>
                    )}
                  </div>
                  {resultadoImport.errores?.length > 0 && (
                    <div className="import-errores">
                      {resultadoImport.errores.map((err, i) => (
                        <div key={i} className="import-error-item">
                          {err.propietario} / {err.proyecto}: {err.error}
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setModalImportar(false)}
                    style={{ marginTop: '16px', padding: '8px 24px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exportar Excel */}
      {modalExportar && (
        <div className="modal-overlay" onClick={() => !exportando && setModalExportar(false)}>
          <div className="modal-exportar" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Exportar Cartera a Excel</h3>
              <button
                className="btn-cerrar"
                onClick={() => setModalExportar(false)}
                disabled={exportando}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-descripcion">
                Selecciona el tipo de informe que deseas exportar:
              </p>

              <div className="opciones-exportar">
                <button
                  className="opcion-exportar sin-pagos"
                  onClick={() => exportarExcel('sin_pagos')}
                  disabled={exportando}
                >
                  <span className="opcion-icono">🔴</span>
                  <span className="opcion-titulo">Sin Pagos</span>
                  <span className="opcion-descripcion">Clientes que no han realizado ningún pago</span>
                </button>

                <button
                  className="opcion-exportar pagos-parciales"
                  onClick={() => exportarExcel('pagos_parciales')}
                  disabled={exportando}
                >
                  <span className="opcion-icono">🟡</span>
                  <span className="opcion-titulo">Pagos Parciales</span>
                  <span className="opcion-descripcion">Clientes con pagos incompletos</span>
                </button>

                <button
                  className="opcion-exportar pagos-completos"
                  onClick={() => exportarExcel('pagos_completos')}
                  disabled={exportando}
                >
                  <span className="opcion-icono">🟢</span>
                  <span className="opcion-titulo">Pagos Completos</span>
                  <span className="opcion-descripcion">Clientes que han pagado el 100%</span>
                </button>

                <button
                  className="opcion-exportar todos"
                  onClick={() => exportarExcel('todos')}
                  disabled={exportando}
                >
                  <span className="opcion-icono">📊</span>
                  <span className="opcion-titulo">Todos los Clientes</span>
                  <span className="opcion-descripcion">Exportar informe completo</span>
                </button>
              </div>

              {exportando && (
                <div className="exportando-mensaje">
                  <div className="spinner-small"></div>
                  <span>Generando reporte...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarteraList;
